import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { TrendingUp, PieChart as PieChartIcon, BarChart as BarChartIcon } from '@mui/icons-material';

interface TrendData {
  date: string;
  sessions: number;
  averageAccuracy: number;
  averageDuration: number;
}

interface DistributionData {
  byStatus: Array<{ name: string; value: number }>;
  byDevice: Array<{ name: string; value: number }>;
  byBrowser: Array<{ name: string; value: number }>;
}

interface ReadingSessionsChartsProps {
  trends: TrendData[];
  distributions: DistributionData | null;
  isLoading: boolean;
}

const COLORS = ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe'];

const ReadingSessionsCharts: React.FC<ReadingSessionsChartsProps> = ({ 
  trends, 
  distributions,
  isLoading 
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return <div className="loadingContainer">Loading charts...</div>;
  }

  return (
    <div className="chartsSection">
      <h2 className="sectionTitle">{t('performanceCharts')}</h2>
      <div className="chartsGrid">
        {/* Trends Chart */}
        <div className="chartCard">
          <div className="chartCardContent">
            <div className="chartHeader">
              <TrendingUp style={{ color: '#667eea', marginInlineEnd: '8px' }} />
              <h3 className="chartTitle">{t('sessionsTrend')}</h3>
            </div>
            {trends && trends.length > 0 ? (
              <div className="chartContainer">
                <ResponsiveContainer>
                  <LineChart data={trends}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis 
                      dataKey="date" 
                      fontSize={10} 
                      axisLine={false} 
                      tickLine={false}
                      tickFormatter={(value) => new Date(value).toLocaleDateString('he-IL', { month: 'short', day: 'numeric' })}
                    />
                    <YAxis fontSize={10} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(255,255,255,0.95)',
                        border: 'none',
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="sessions" 
                      stroke="#667eea" 
                      strokeWidth={3}
                      dot={{ fill: '#667eea', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: '#667eea', strokeWidth: 2 }}
                      name={t('sessions')}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="averageAccuracy" 
                      stroke="#764ba2" 
                      strokeWidth={3}
                      dot={{ fill: '#764ba2', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: '#764ba2', strokeWidth: 2 }}
                      name={t('averageAccuracy')}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="noDataContainer">
                <span>{t('noData')}</span>
              </div>
            )}
          </div>
        </div>

        {/* Status Distribution */}
        {distributions && distributions.byStatus.length > 0 && (
          <div className="chartCard">
            <div className="chartCardContent">
              <div className="chartHeader">
                <PieChartIcon style={{ color: '#f093fb', marginInlineEnd: '8px' }} />
                <h3 className="chartTitle">{t('distributionByStatus')}</h3>
              </div>
              <div className="pieChartContainer">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={distributions.byStatus.map(item => ({
                        ...item,
                        name: t(item.name) // תרגום שם הסטטוס
                      }))}
                      cx="50%"
                      cy="50%"
                      innerRadius={62}
                      outerRadius={96}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${((Number(percent) || 0) * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {distributions.byStatus.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(255,255,255,0.95)',
                        border: 'none',
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                      }}
                      formatter={(value: any, name: string) => [
                        value,
                        t(name) // תרגום גם ב-tooltip
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* Device Distribution */}
        {distributions && distributions.byDevice.length > 0 && (
          <div className="chartCard">
            <div className="chartCardContent">
              <div className="chartHeader">
                <BarChartIcon style={{ color: '#4facfe', marginInlineEnd: '8px' }} />
                <h3 className="chartTitle">{t('distributionByDevice')}</h3>
              </div>
              <div className="chartContainer">
                <ResponsiveContainer>
                  <BarChart data={distributions.byDevice}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="name" fontSize={10} axisLine={false} tickLine={false} />
                    <YAxis fontSize={10} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(255,255,255,0.95)',
                        border: 'none',
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                      }}
                    />
                    <Bar dataKey="value" fill="#667eea" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* Browser Distribution */}
        {distributions && distributions.byBrowser.length > 0 && (
          <div className="chartCard">
            <div className="chartCardContent">
              <div className="chartHeader">
                <BarChartIcon style={{ color: '#4facfe', marginInlineEnd: '8px' }} />
                <h3 className="chartTitle">{t('distributionByBrowser')}</h3>
              </div>
              <div className="chartContainer">
                <ResponsiveContainer>
                  <BarChart data={distributions.byBrowser}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="name" fontSize={10} axisLine={false} tickLine={false} />
                    <YAxis fontSize={10} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(255,255,255,0.95)',
                        border: 'none',
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                      }}
                    />
                    <Bar dataKey="value" fill="#764ba2" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReadingSessionsCharts;
